import * as React from 'react';

import { cn } from '@playbooks/ui/lib/utils';

import { type VariantProps, cva } from 'class-variance-authority';

const sectionPanelVariants = cva(
	'group/section-panel relative mb-3 w-full overflow-hidden rounded-panel border bg-surface',
	{
		variants: {
			density: {
				compact:
					'[--section-panel-x:0.75rem] [--section-panel-y:0.5rem] [--section-panel-row-x:0.75rem] [--section-panel-row-y:0.5rem]',
				default: '',
				comfortable:
					'[--section-panel-x:1rem] [--section-panel-y:1rem] [--section-panel-row-x:1rem] [--section-panel-row-y:1rem]',
			},
			padding: {
				default: '',
				flush: '[--section-panel-x:0rem]',
			},
		},
		defaultVariants: { density: 'default', padding: 'default' },
	},
);

type SectionPanelProps = React.ComponentProps<'section'> & VariantProps<typeof sectionPanelVariants>;

const SectionPanel = ({ className = '', density = 'default', padding = 'default', ...props }: SectionPanelProps) => {
	// Theme
	const rootClassName = cn(sectionPanelVariants({ density, padding }), className);

	// Render
	return (
		<section
			data-slot='section-panel'
			data-density={density}
			data-padding={padding}
			className={rootClassName}
			{...props}
		/>
	);
};

const SectionPanelHeader = ({ className = '', ...props }: React.ComponentProps<'header'>) => {
	// Theme
	const rootClassName = cn('flex-between gap-2 border-b px-(--section-panel-x) py-(--section-panel-y)', className);

	// Render
	return <header data-slot='section-panel-header' className={rootClassName} {...props} />;
};

const SectionPanelBody = ({ className = '', ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('w-full px-(--section-panel-x)', className);

	// Render
	return <div data-slot='section-panel-body' className={rootClassName} {...props} />;
};

const SectionPanelFooter = ({ className = '', ...props }: React.ComponentProps<'footer'>) => {
	// Theme
	const rootClassName = cn('flex-between gap-2 border-t px-(--section-panel-x) py-(--section-panel-y)', className);

	// Render
	return <footer data-slot='section-panel-footer' className={rootClassName} {...props} />;
};

const SectionPanelRow = ({ className = '', ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn(
		'flex items-center justify-between gap-3 border-b px-(--section-panel-row-x) py-(--section-panel-row-y) last:border-b-0',
		className,
	);

	// Render
	return <div data-slot='section-panel-row' className={rootClassName} {...props} />;
};

export { SectionPanel, SectionPanelBody, SectionPanelFooter, SectionPanelHeader, SectionPanelRow };
export type { SectionPanelProps };
